import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import * as projectApi from '../../lib/api/project';
import {
  listBuysOrderSheet,
  setBuysOrderSheetFilters,
} from '../../modules/projectModule/buys-order-sheet';
import { BuysOrderSheetListBox } from '../../components/buys_order_sheet/BuysOrderSheetListBox';
import BuysOrderSheetList from '../../components/buys_order_sheet/BuysOrderSheetList';
import OrderSheetEditModal from '../../components/buys_order_sheet/OrderSheetEditModal';

const BuysOrderSheetListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { orderSheets, defaultFilters, error, loading } = useSelector(
    ({ projectModule, loading }) => ({
      orderSheets: projectModule.buysOrderSheet.ordersheets,
      defaultFilters: projectModule.buysOrderSheet.defaultFilters,
      error: projectModule.buysOrderSheet.error,
      loading: loading['project/LIST_BUYS_ORDER_SHEET'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listBuysOrderSheet(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (orderSheets) {
      setTableData(orderSheets);
    }
  }, [orderSheets]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setBuysOrderSheetFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const handleExlDown = useCallback(
    async (param, event) => {
      try {
        const blob = await projectApi.downloadOrderSheet(
          param.row.prjUuid,
          param.row.spoUuid,
        );

        const filename = blob.headers['filename'];
        downloadFile(blob, decodeURIComponent(filename), 'xlsx');
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar],
  );

  //엑셀 다운로드
  //파일명 확인
  const downloadFile = (file, fileNm, filedoc) => {
    let blob = new Blob([file.data]);
    let fileName = `${fileNm}.${filedoc}`;

    if (window.navigator.msSaveOrOpenBlob) {
      // IE 10+는 해당 함수 제공
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.target = '_self';
      if (fileName) link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link); //메모리 누수 방지
    }
  };

  const [showEdit, setShowEdit] = useState(false);
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param) => {
    setSelectedEdit(param);
    setShowEdit(true);
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setSelectedEdit(null);
  };

  const handleSave = async (changeDate) => {
    try {
      const dateToSubmit = {
        prjUuid: selectedEdit.prjUuid,
        spoUuid: selectedEdit.spoUuid,
        sheetCrtDate: dayjs(changeDate).format('YYYY-MM-DD'),
      };
      const response = await projectApi.editOrderSheet(dateToSubmit);
      if (response.data.info) {
        dispatch(listBuysOrderSheet(makeFilters(defaultFilters)));
        enqueueSnackbar('저장 되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
      },
      {
        field: 'spoUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
      },
      {
        field: 'prjUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
      },
      {
        field: 'sheetCrtDateAction',
        headerName: '',
        minWidth: 30,
        flex: 0.2,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <IconButton
            aria-label="edit"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
        ),
      },
      {
        field: 'sheetCrtDate',
        headerName: '발주일자',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
        renderCell: (params) => (
          <>
            {params.value}
            <IconButton
              aria-label="preview"
              color="success"
              size="small"
              onClick={() => handleExlDown(params)}
            >
              <PreviewIcon />
            </IconButton>
          </>
        ),
      },

      {
        field: 'sellPartnerUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
      },
      {
        field: 'sellPartnerNm',
        headerName: '매입처',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },

      {
        field: 'sheetUsrNm',
        headerName: '담당자',
        minWidth: 120,
        flex: 0.8,
        type: 'string',
        editable: false,
      },
      {
        field: 'sheetTotal',
        headerName: '발주금액',
        minWidth: 120,
        flex: 0.8,
        type: 'number',
        editable: false,
        valueFormatter: (params) => {
          const valueFormatted = Number(params.value).toLocaleString('ko-KR');
          return `₩ ${valueFormatted}`;
        },
      },

      {
        field: 'useYn',
        headerName: '사용여부',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'crtNm',
        headerName: '작성자',
        minWidth: 80,
        flex: 0.3,
        type: 'string',
        editable: false,
      },
      {
        field: 'crtDt',
        headerName: '작성일',
        minWidth: 150,
        flex: 0.8,
        type: 'string',
        editable: false,
      },
    ],
    [],
  );

  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        let searchData = {
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setBuysOrderSheetFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="수주 관리/매입(보낸) 발주서 현황/매입 발주서 리스트"
        icon="project"
      />
      <BuysOrderSheetListBox formik={formik} />
      <BuysOrderSheetList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
      />
      {showEdit && (
        <OrderSheetEditModal
          currentData={selectedEdit}
          open={showEdit}
          handleClose={handleEditClose}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default BuysOrderSheetListContainer;
